import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Search, Gamepad2, X, Package, Download, ImageUp } from "lucide-react";
import RichTextEditor from "../RichTextEditor";

const CONDITION_OPTIONS = [
  { value: "new", label: "Nové – nerozbaleno" },
  { value: "very_good", label: "Velmi dobrý" },
  { value: "good", label: "Dobrý" },
  { value: "acceptable", label: "Přijatelný" },
];

const TYPE_OPTIONS = [
  { value: "physical", label: "Fyzické (disc / HW)" },
  { value: "digital", label: "Digitální (Steam / PSN / Xbox kód)" },
];

const PLATFORM_OPTIONS = ["PC", "PlayStation 5", "PlayStation 4", "Xbox Series X|S", "Xbox One", "Nintendo Switch", "Steam", "Epic Games", "Xbox", "PSN"];

const EMPTY = {
  slug: "",
  name: "",
  publisher: "",
  genres: [],
  platforms: [],
  condition: "new",
  product_type: "physical",
  short_description: "",
  description: "",
  price: 0,
  price_original: null,
  currency: "CZK",
  images: [],
  features: [],
  labels: [],
  stock_qty: 1,
  active: true,
  featured: false,
  seo_title: "",
  seo_description: "",
};

const I = "w-full px-3 py-2 rounded-xl border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 disabled:bg-zinc-50";

function FF({ label, children }) {
  return (
    <label className="block mb-3">
      <div className="text-xs font-semibold text-zinc-600 mb-1">{label}</div>
      {children}
    </label>
  );
}

function ChipsInput({ value, onChange, placeholder, testid }) {
  const [t, setT] = useState("");
  const add = () => {
    const v = t.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setT("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[24px]">
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-purple-100 text-purple-800">
            {v}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} className="hover:text-purple-950"><X className="w-3 h-3"/></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} className={I} data-testid={testid}/>
        <button type="button" onClick={add} className="px-3 py-2 rounded-xl border border-zinc-200 text-sm hover:bg-zinc-50">+</button>
      </div>
    </div>
  );
}

function UploadImage({ onUploaded, disabled }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const rel = r.data.url || r.data.path || "";
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const finalUrl = rel.startsWith("http") ? rel : (rel.startsWith("/api/") ? `${backend}${rel}` : rel);
      onUploaded(finalUrl);
    } catch (err) {
      toast.error("Nahrání selhalo");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed ${disabled ? "opacity-50 cursor-not-allowed border-zinc-200" : "cursor-pointer border-zinc-300 hover:border-purple-500 hover:bg-purple-50/40"} text-sm`}>
      <ImageUp className="w-4 h-4"/>
      {busy ? "Nahrávám…" : disabled ? "Max. 5 obrázků" : "Nahrát obrázek"}
      <input type="file" accept="image/*" className="hidden" disabled={disabled} onChange={handle} data-testid="gaming-form-image-upload"/>
    </label>
  );
}

export default function GamingProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | product

  const load = () => {
    setLoading(true);
    api.get("/admin/gaming/products").then((r) => setProducts(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!window.confirm(`Smazat "${p.name}"?`)) return;
    await api.delete(`/admin/gaming/products/${p.id}`);
    toast.success("Smazáno");
    load();
  };

  const filtered = products.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.publisher || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-gaming-products">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <div className="font-display font-bold text-2xl inline-flex items-center gap-2"><Gamepad2 className="w-6 h-6 text-purple-600"/> Gaming produkty</div>
          <div className="text-sm text-zinc-500 mt-0.5">Nové i bazarové hry, konzole, herní příslušenství.</div>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5" data-testid="gaming-new-btn"><Plus className="w-4 h-4"/> Nový produkt</button>
      </div>

      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat podle názvu nebo vydavatele…" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" data-testid="gaming-search"/>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Načítání…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 rounded-2xl bg-zinc-50 border border-dashed border-zinc-200">
          <Gamepad2 className="w-10 h-10 mx-auto text-zinc-300 mb-2"/>
          <div className="text-sm text-zinc-600">Zatím žádný produkt. Klikněte na tlačítko Nový produkt v pravém horním rohu.</div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2.5">Produkt</th>
                <th className="text-left px-3 py-2.5">Typ</th>
                <th className="text-left px-3 py-2.5">Platforma</th>
                <th className="text-right px-3 py-2.5">Cena</th>
                <th className="text-right px-3 py-2.5">Sklad</th>
                <th className="text-center px-3 py-2.5">Aktivní</th>
                <th className="text-right px-3 py-2.5">Akce</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-zinc-100 hover:bg-zinc-50/70" data-testid={`gaming-row-${p.slug}`}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      {p.images?.[0] && <img src={p.images[0]} alt="" className="w-9 h-9 rounded-lg object-cover" onError={(e) => e.currentTarget.style.display="none"}/>}
                      <div>
                        <div className="font-semibold">{p.name}</div>
                        <div className="text-xs text-zinc-500">{p.publisher || "—"} · {p.condition}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold">
                      {p.product_type === "digital" ? <><Download className="w-3 h-3"/> Digital</> : <><Package className="w-3 h-3"/> Fyzické</>}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs">{(p.platforms || []).join(", ") || "—"}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{p.price} {p.currency}</td>
                  <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${p.stock_qty > 0 ? "text-emerald-600" : "text-rose-500"}`}>{p.stock_qty}</td>
                  <td className="px-3 py-2.5 text-center">{p.active ? "✅" : "—"}</td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={() => setEditing({ ...p })} className="p-1.5 text-zinc-500 hover:text-purple-600" data-testid={`gaming-edit-${p.slug}`}><Edit3 className="w-4 h-4"/></button>
                    <button onClick={() => del(p)} className="p-1.5 text-zinc-500 hover:text-rose-600" data-testid={`gaming-del-${p.slug}`}><Trash2 className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EditModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isEdit = !!initial.id;

  const save = async (e) => {
    e.preventDefault();
    if (!form.slug || !form.name || !form.price) return toast.error("Vyplňte slug, název a cenu");
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), price_original: form.price_original ? Number(form.price_original) : null, stock_qty: Number(form.stock_qty || 0) };
      delete payload.id; delete payload.created_at; delete payload.sold_count;
      if (isEdit) await api.put(`/admin/gaming/products/${initial.id}`, payload);
      else await api.post("/admin/gaming/products", payload);
      toast.success(isEdit ? "Uloženo" : "Vytvořeno");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="gaming-edit-modal">
        <form onSubmit={save} className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="font-display font-bold text-2xl">{isEdit ? "Upravit produkt" : "Nový gaming produkt"}</div>
            <button type="button" onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full"><X className="w-5 h-5"/></button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FF label="Slug (URL) *"><input value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} className={I} placeholder="fifa-25-ps5" required data-testid="gaming-form-slug"/></FF>
            <FF label="Název *"><input value={form.name} onChange={(e) => set("name", e.target.value)} className={I} required data-testid="gaming-form-name"/></FF>
          </div>
          <FF label="Vydavatel"><input value={form.publisher} onChange={(e) => set("publisher", e.target.value)} className={I} placeholder="EA Sports / Sony / Ubisoft…" data-testid="gaming-form-publisher"/></FF>

          <div className="grid grid-cols-2 gap-3">
            <FF label="Typ produktu">
              <select value={form.product_type} onChange={(e) => set("product_type", e.target.value)} className={I} data-testid="gaming-form-type">
                {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FF>
            <FF label="Stav">
              <select value={form.condition} onChange={(e) => set("condition", e.target.value)} className={I} data-testid="gaming-form-condition">
                {CONDITION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FF>
          </div>

          <FF label="Platformy (Enter pro přidání)">
            <ChipsInput value={form.platforms} onChange={(v) => set("platforms", v)} placeholder="PS5, PC, Xbox…" testid="gaming-form-platform-input"/>
            <div className="mt-2 flex flex-wrap gap-1">
              {PLATFORM_OPTIONS.map((pl) => (
                <button key={pl} type="button" onClick={() => !form.platforms.includes(pl) && set("platforms", [...form.platforms, pl])} className="text-xs px-2 py-1 rounded-full bg-zinc-100 hover:bg-purple-100 hover:text-purple-800 text-zinc-600">+ {pl}</button>
              ))}
            </div>
          </FF>

          <FF label="Žánry (Enter pro přidání)">
            <ChipsInput value={form.genres} onChange={(v) => set("genres", v)} placeholder="RPG, FPS, sport, závody…" testid="gaming-form-genre-input"/>
          </FF>

          <div className="grid grid-cols-3 gap-3">
            <FF label="Cena (Kč) *"><input type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className={I} required data-testid="gaming-form-price"/></FF>
            <FF label="Původní cena (přeškrtnutá)"><input type="number" step="0.01" value={form.price_original || ""} onChange={(e) => set("price_original", e.target.value || null)} className={I} data-testid="gaming-form-price-original"/></FF>
            <FF label="Sklad (ks) *"><input type="number" min="0" value={form.stock_qty} onChange={(e) => set("stock_qty", e.target.value)} className={I} required data-testid="gaming-form-stock"/></FF>
          </div>

          <FF label="Krátký popis (do karty)"><textarea rows={2} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className={I} data-testid="gaming-form-short"/></FF>
          <FF label="Popis (na stránce produktu)">
            <RichTextEditor value={form.description} onChange={(v) => set("description", v)}/>
          </FF>

          <FF label="Obrázky (až 5 – první je hlavní)">
            <div className="grid grid-cols-5 gap-2 mb-2">
              {form.images.map((img, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-zinc-200 group">
                  <img src={img} alt="" className="w-full h-full object-cover"/>
                  <button type="button" onClick={() => set("images", form.images.filter((_, x) => x !== i))} className="absolute top-1 right-1 p-1 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3"/></button>
                  {i === 0 && <span className="absolute bottom-1 left-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-600 text-white">HLAVNÍ</span>}
                </div>
              ))}
            </div>
            <UploadImage onUploaded={(url) => set("images", [...form.images, url].slice(0, 5))} disabled={form.images.length >= 5}/>
          </FF>

          <FF label="Vlastnosti (bullet body – Enter pro přidání)">
            <ChipsInput value={form.features} onChange={(v) => set("features", v)} placeholder="Zabalený v originální krabici, český manuál…" testid="gaming-form-feature-input"/>
          </FF>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="gaming-form-active"/>
              Aktivní (viditelný na webu)
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="gaming-form-featured"/>
              Doporučené (zobrazovat výše)
            </label>
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-zinc-100">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-zinc-200 hover:bg-zinc-50 text-sm font-semibold">Zrušit</button>
            <button type="submit" disabled={saving} className="btn-primary px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="gaming-form-submit">{saving ? "Ukládám…" : (isEdit ? "Uložit" : "Vytvořit")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
